import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { usePermissions } from "../hooks/usePermissions";
import {
    Radio, AlertTriangle, Plus, X, Loader2, CheckCircle2, Filter,
    ShieldAlert, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

const SEVERITIES = ["Low", "Medium", "High", "Critical"];

export default function FieldIntel() {
    const perm = usePermissions();
    const [reports, setReports] = useState([]);
    const [products, setProducts] = useState([]);
    const [vendors, setVendors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [severityFilter, setSeverityFilter] = useState("All");
    const [resolvedFilter, setResolvedFilter] = useState("All"); // All | Open | Resolved
    const [productFilter, setProductFilter] = useState("All");

    // Modals
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ product_id: "", severity: "Medium", issue_description: "", environment_conditions: "" });
    const [resolveTarget, setResolveTarget] = useState(null);
    const [resolveNotes, setResolveNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const [modalErr, setModalErr] = useState("");

    const refresh = async () => {
        try {
            const [r, p, v] = await Promise.all([
                api.get("/field-reports").then((r) => r.data),
                api.get("/products").then((r) => r.data),
                api.get("/vendors").then((r) => r.data).catch(() => []),
            ]);
            setReports(r);
            setProducts(p);
            setVendors(v);
        } catch (err) {
            const status = err?.response?.status;
            if (status !== 401) setError(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

    const productById = useMemo(() => Object.fromEntries(products.map((p) => [p.id, p])), [products]);

    const filteredReports = useMemo(() => {
        return reports.filter((r) => {
            if (severityFilter !== "All" && r.severity !== severityFilter) return false;
            if (resolvedFilter === "Open" && r.resolved) return false;
            if (resolvedFilter === "Resolved" && !r.resolved) return false;
            if (productFilter !== "All" && r.product_id !== productFilter) return false;
            return true;
        });
    }, [reports, severityFilter, resolvedFilter, productFilter]);

    const stats = useMemo(() => {
        const open = reports.filter((r) => !r.resolved);
        return {
            total: reports.length,
            open: open.length,
            critical_open: open.filter((r) => r.severity === "Critical").length,
            high_open: open.filter((r) => r.severity === "High").length,
        };
    }, [reports]);

    // ---- Submit new report ----
    const openCreate = () => {
        const firstProduct = products[0];
        setForm({
            product_id: firstProduct?.id || "",
            severity: "Medium",
            issue_description: "",
            environment_conditions: "",
        });
        setCreating(true);
        setModalErr("");
    };

    const submitReport = async (e) => {
        e.preventDefault();
        if (form.issue_description.trim().length < 10) {
            setModalErr("Issue description must be at least 10 characters.");
            return;
        }
        setBusy(true);
        setModalErr("");
        try {
            const { data } = await api.post("/field-reports", form);
            if (data.severity === "Critical") {
                toast.warning(`${data.report_id} filed — product auto-suspended & kill switch ENGAGED.`);
            } else {
                toast.success(`${data.report_id} filed`);
            }
            setCreating(false);
            refresh();
        } catch (err) {
            setModalErr(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setBusy(false);
        }
    };

    // ---- Resolve flow ----
    const submitResolve = async () => {
        if (!resolveTarget) return;
        const trimmed = resolveNotes.trim();
        if (trimmed.length < 3) {
            setModalErr("Resolution notes are required (min 3 characters).");
            return;
        }
        setBusy(true);
        setModalErr("");
        try {
            await api.patch(`/field-reports/${resolveTarget.report_id}`, { resolved: true, resolution_notes: trimmed });
            toast.success(`${resolveTarget.report_id} marked resolved`);
            setResolveTarget(null);
            setResolveNotes("");
            refresh();
        } catch (err) {
            setModalErr(formatApiErrorDetail(err?.response?.data?.detail) || err?.message);
        } finally {
            setBusy(false);
        }
    };

    if (loading) {
        return (
            <div className="space-y-4" data-testid="field-intel-loading">
                <div className="overline">Operations / Field Intel</div>
                <div className="heading-display text-4xl">Field Intel</div>
                <div className="text-snow-dim font-mono text-sm">Loading reports…</div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="border border-red-500/40 bg-red-500/5 p-6" data-testid="field-intel-error">
                <div className="overline text-red-400">Field Intel Unavailable</div>
                <div className="heading-display text-xl mt-2">Could not load field reports</div>
                <div className="text-xs text-snow-dim mt-2 font-mono">{error}</div>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="field-intel-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="overline">Operations / Field Intel</div>
                    <h1 className="heading-display text-4xl mt-1 flex items-center gap-3">
                        <Radio className="w-8 h-8 text-signal" />
                        Field Intel
                    </h1>
                    <p className="text-snow-dim text-sm mt-2 max-w-2xl">
                        Operator field reports drive automatic recertification.
                        <span className="text-signal"> Critical </span>
                        reports auto-suspend the product and trigger executive alerts.
                    </p>
                </div>
                <button
                    data-testid="file-report-btn"
                    onClick={openCreate}
                    disabled={!perm.fileFieldReport}
                    className={`px-5 py-2 text-xs font-bold uppercase tracking-[0.2em] flex items-center gap-2 transition-colors ${
                        perm.fileFieldReport
                            ? "bg-signal text-jet hover:bg-signal-hover"
                            : "bg-[#2A2A2A] text-snow-muted cursor-not-allowed"
                    }`}
                >
                    <Plus className="w-3 h-3" /> File Report
                </button>
            </div>

            {/* KPI strip */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KPI testid="fi-kpi-total" label="Total Reports" value={stats.total} icon={Activity} />
                <KPI testid="fi-kpi-open" label="Open" value={stats.open} accent={stats.open > 0} icon={Radio} />
                <KPI testid="fi-kpi-critical" label="Critical Open" value={stats.critical_open} alert={stats.critical_open > 0} icon={ShieldAlert} />
                <KPI testid="fi-kpi-high" label="High Open" value={stats.high_open} alert={stats.high_open > 0} icon={AlertTriangle} />
            </div>

            {/* Vendor sentiment row (if any) */}
            {vendors.length > 0 && (
                <div className="border border-[#2A2A2A] bg-jet-elev1 p-5" data-testid="vendor-sentiment-panel">
                    <div className="flex items-center justify-between mb-3">
                        <div className="overline">Vendor Sentiment</div>
                        <span className="text-[10px] text-snow-muted font-mono uppercase tracking-wider">Auto = 100 − (Critical×12 + High×5)</span>
                    </div>
                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                        {vendors.map((v) => (
                            <VendorChip key={v.id} v={v} />
                        ))}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex items-center gap-3 flex-wrap" data-testid="filter-bar">
                <Filter className="w-3 h-3 text-snow-muted" />
                <SegmentedControl
                    name="severity"
                    options={["All", ...SEVERITIES]}
                    value={severityFilter}
                    onChange={setSeverityFilter}
                />
                <SegmentedControl
                    name="resolved"
                    options={["All", "Open", "Resolved"]}
                    value={resolvedFilter}
                    onChange={setResolvedFilter}
                />
                <select
                    data-testid="filter-product-select"
                    value={productFilter}
                    onChange={(e) => setProductFilter(e.target.value)}
                    className="bg-jet-elev1 border border-[#2A2A2A] text-snow-dim font-mono text-xs px-3 py-1.5 focus:border-signal outline-none"
                >
                    <option value="All">All Products</option>
                    {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <span className="text-[10px] text-snow-muted font-mono uppercase tracking-wider ml-auto">
                    Showing {filteredReports.length} of {reports.length}
                </span>
            </div>

            {/* Field Reports Grid */}
            <div className="border border-[#2A2A2A] bg-jet-elev1" data-testid="reports-grid">
                {filteredReports.length === 0 ? (
                    <div className="px-5 py-12 text-center text-snow-muted text-xs font-mono">
                        No reports match the current filter.
                    </div>
                ) : (
                    <ul className="divide-y divide-[#2A2A2A]">
                        {filteredReports.map((r) => (
                            <ReportRow
                                key={r.id}
                                report={r}
                                product={productById[r.product_id]}
                                canResolve={perm.resolveFieldReport}
                                onResolve={() => { setResolveTarget(r); setResolveNotes(""); setModalErr(""); }}
                            />
                        ))}
                    </ul>
                )}
            </div>

            {/* Create modal */}
            {creating && (
                <Modal title="File Field Report" onClose={() => setCreating(false)}>
                    <form onSubmit={submitReport} className="space-y-4" data-testid="report-form">
                        <Field label="Product">
                            <select
                                data-testid="report-product-select"
                                required
                                value={form.product_id}
                                onChange={(e) => setForm({ ...form, product_id: e.target.value })}
                                className="cerb-input"
                            >
                                <option value="" disabled>— Select a product —</option>
                                {products.map((p) => (
                                    <option key={p.id} value={p.id}>
                                        {p.name} {p.kill_switch_active ? "⚠ KILL" : ""}
                                    </option>
                                ))}
                            </select>
                        </Field>
                        <Field label="Severity">
                            <div className="grid grid-cols-4 gap-2" data-testid="severity-pills">
                                {SEVERITIES.map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        data-testid={`severity-${s.toLowerCase()}`}
                                        onClick={() => setForm({ ...form, severity: s })}
                                        className={`border px-2 py-2 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                                            form.severity === s
                                                ? severityClass(s, true)
                                                : "border-[#2A2A2A] text-snow-dim hover:border-snow"
                                        }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </Field>
                        {form.severity === "Critical" && (
                            <div className="border border-signal bg-signal/5 px-3 py-2 text-[11px] font-mono text-signal" data-testid="critical-warning">
                                <span className="font-bold">CRITICAL TRIGGER:</span> filing this report will automatically
                                <span className="text-snow"> suspend </span> the product,
                                <span className="text-snow"> engage </span> the kill switch, and alert all executives.
                            </div>
                        )}
                        <Field label="Issue Description">
                            <textarea
                                data-testid="issue-description-input"
                                rows={4}
                                required
                                minLength={10}
                                value={form.issue_description}
                                onChange={(e) => setForm({ ...form, issue_description: e.target.value })}
                                className="cerb-input"
                                placeholder="Describe what was observed in the field — be specific."
                                maxLength={2000}
                            />
                            <div className="text-[10px] text-snow-muted mt-1 font-mono uppercase tracking-wider flex justify-between">
                                <span>Required • min 10 chars</span>
                                <span className="tabular-nums">{form.issue_description.length}/2000</span>
                            </div>
                        </Field>
                        <Field label="Environment Conditions">
                            <textarea
                                data-testid="environment-input"
                                rows={2}
                                value={form.environment_conditions}
                                onChange={(e) => setForm({ ...form, environment_conditions: e.target.value })}
                                className="cerb-input"
                                placeholder="Cluster, region, load profile, hardware revision, etc."
                                maxLength={2000}
                            />
                        </Field>
                        {modalErr && <div className="text-red-400 text-xs font-mono">{modalErr}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button type="button" onClick={() => setCreating(false)} className="cerb-btn-secondary" data-testid="report-cancel-btn">Cancel</button>
                            <button type="submit" disabled={busy} className="cerb-btn-primary" data-testid="report-save-btn">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "File Report"}
                            </button>
                        </div>
                    </form>
                </Modal>
            )}

            {/* Resolve modal */}
            {resolveTarget && (
                <Modal title={`Resolve ${resolveTarget.report_id}`} onClose={() => { setResolveTarget(null); setResolveNotes(""); }} accent>
                    <div className="space-y-4" data-testid="resolve-modal">
                        <div className="text-xs text-snow-dim font-mono">
                            <SeverityBadge severity={resolveTarget.severity} /> on{" "}
                            <span className="text-snow">{productById[resolveTarget.product_id]?.name || "—"}</span>
                        </div>
                        <div className="text-[11px] text-snow-muted font-mono italic border-l-2 border-signal/40 pl-3">
                            "{resolveTarget.issue_description}"
                        </div>
                        <div>
                            <label className="overline block mb-2">Resolution Notes <span className="text-signal">*</span></label>
                            <textarea
                                data-testid="resolve-notes-input"
                                rows={3}
                                autoFocus
                                value={resolveNotes}
                                onChange={(e) => setResolveNotes(e.target.value)}
                                className="cerb-input"
                                maxLength={2000}
                                placeholder="What was done to fix it? Include validation evidence."
                            />
                        </div>
                        {modalErr && <div className="text-red-400 text-xs font-mono">{modalErr}</div>}
                        <div className="flex justify-end gap-2 pt-3 border-t border-hairline">
                            <button onClick={() => { setResolveTarget(null); setResolveNotes(""); }} className="cerb-btn-secondary" data-testid="resolve-cancel-btn">Cancel</button>
                            <button onClick={submitResolve} disabled={busy || resolveNotes.trim().length < 3} className="cerb-btn-primary" data-testid="resolve-confirm-btn">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Mark Resolved"}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
}

// ---------- sub-components ----------

function ReportRow({ report, product, canResolve, onResolve }) {
    const isCritical = report.severity === "Critical";
    const rowCls = isCritical && !report.resolved
        ? "px-5 py-4 border-l-2 border-signal bg-signal/5 hover:bg-signal/10"
        : "px-5 py-4 hover:bg-white/[0.03]";
    return (
        <li className={rowCls} data-testid={`report-row-${report.report_id}`}>
            <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        <span
                            className={`font-mono text-sm tabular-nums ${isCritical && !report.resolved ? "text-signal font-bold" : "text-snow"}`}
                            data-testid={`report-id-${report.report_id}`}
                        >
                            {report.report_id}
                        </span>
                        <SeverityBadge severity={report.severity} resolved={report.resolved} />
                        {report.resolved
                            ? <span className="inline-flex items-center gap-1 border border-green-500/40 text-green-400 bg-green-500/5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em]">
                                <CheckCircle2 className="w-3 h-3" /> Resolved
                              </span>
                            : <span className="inline-flex items-center border border-yellow-500/40 text-yellow-400 bg-yellow-500/5 px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.2em]">
                                Open
                              </span>
                        }
                        <span className="text-xs font-mono text-snow-dim">{product?.name || "—"}</span>
                        {product && (
                            <Link
                                to={`/products/${product.id}`}
                                className="text-[10px] text-signal underline-offset-2 hover:underline font-mono uppercase tracking-wider"
                                data-testid={`report-product-link-${report.report_id}`}
                            >
                                Open →
                            </Link>
                        )}
                    </div>
                    <div className={`text-sm mt-2 font-mono ${isCritical && !report.resolved ? "text-signal" : "text-snow-dim"}`}>
                        {report.issue_description}
                    </div>
                    {report.environment_conditions && (
                        <div className="text-[11px] text-snow-muted mt-1 font-mono">
                            <span className="overline mr-2">Env:</span>{report.environment_conditions}
                        </div>
                    )}
                    {report.resolved && report.resolution_notes && (
                        <div className="text-[11px] text-green-400/90 mt-1 font-mono italic">
                            <span className="overline text-green-400 mr-2">Resolution:</span>"{report.resolution_notes}"
                        </div>
                    )}
                </div>
                <div className="text-right text-[11px] font-mono text-snow-muted whitespace-nowrap tabular-nums flex-shrink-0">
                    <div>{report.operator_email}</div>
                    <div className="mt-0.5">{relativeTime(report.timestamp)}</div>
                    {!report.resolved && canResolve && (
                        <button
                            data-testid={`resolve-btn-${report.report_id}`}
                            onClick={onResolve}
                            className="mt-2 px-2 py-1 text-[10px] font-mono uppercase tracking-wider border border-[#2A2A2A] text-snow-dim hover:border-signal hover:text-signal transition-colors"
                        >
                            Resolve
                        </button>
                    )}
                </div>
            </div>
        </li>
    );
}

function SeverityBadge({ severity, resolved }) {
    const cls = severityClass(severity, !resolved);
    return (
        <span className={`inline-flex items-center border px-2 py-0.5 text-[10px] uppercase tracking-[0.2em] font-mono ${cls}`}
            data-testid={`severity-badge-${severity.toLowerCase()}`}>
            {severity}
        </span>
    );
}

// Spec: Graphite (#2A2A2A) borders + Accent Orange (#FF6A00) text for Critical reports.
function severityClass(severity, active = true) {
    if (severity === "Critical") {
        return active
            ? "border-[#2A2A2A] text-signal bg-jet-elev1"
            : "border-[#2A2A2A] text-signal/60 bg-jet-elev1";
    }
    if (severity === "High") return "border-orange-500/40 text-orange-300 bg-orange-500/5";
    if (severity === "Medium") return "border-yellow-500/40 text-yellow-300 bg-yellow-500/5";
    return "border-[#2A2A2A] text-snow-dim bg-jet-elev1";
}

function VendorChip({ v }) {
    const score = v.risk_score;
    const color = score >= 90 ? "text-green-400 border-green-500/40 bg-green-500/5"
        : score >= 70 ? "text-yellow-400 border-yellow-500/40 bg-yellow-500/5"
        : "text-signal border-signal/60 bg-signal/5";
    return (
        <div className="border border-[#2A2A2A] bg-jet-elev2 p-3 flex items-center justify-between" data-testid={`vendor-chip-${v.name.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="min-w-0">
                <div className="font-mono text-sm truncate">{v.name}</div>
                <div className="text-[10px] text-snow-muted font-mono uppercase tracking-wider mt-0.5">
                    Crit:{v.report_count_critical} • High:{v.report_count_high}
                </div>
            </div>
            <div className={`border px-2 py-1 text-xs font-mono tabular-nums ${color}`}>
                {score}
            </div>
        </div>
    );
}

function KPI({ label, value, alert, accent, icon: Icon, testid }) {
    return (
        <div
            data-testid={testid}
            className={`border ${alert ? "border-signal bg-signal/5" : "border-[#2A2A2A] bg-jet-elev1"} p-5 flex items-center justify-between`}
        >
            <div>
                <div className="overline">{label}</div>
                <div className={`heading-display text-4xl mt-3 tabular-nums ${alert || accent ? "text-signal" : "text-snow"}`}>
                    {value}
                </div>
            </div>
            {Icon && <Icon className={`w-7 h-7 ${alert ? "text-signal animate-pulse-signal" : "text-snow-muted"}`} />}
        </div>
    );
}

function SegmentedControl({ name, options, value, onChange }) {
    return (
        <div className="flex border border-[#2A2A2A]" data-testid={`segmented-${name}`}>
            {options.map((opt) => (
                <button
                    key={opt}
                    data-testid={`${name}-${opt.toLowerCase()}`}
                    onClick={() => onChange(opt)}
                    className={`px-3 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors ${
                        value === opt
                            ? "bg-signal text-jet"
                            : "text-snow-dim hover:text-snow"
                    }`}
                >
                    {opt}
                </button>
            ))}
        </div>
    );
}

function Modal({ title, children, onClose, accent }) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-jet/80 backdrop-blur-sm p-4" onClick={onClose}>
            <div
                className={`relative w-full max-w-xl bg-jet-elev1 border ${accent ? "border-signal" : "border-hairline"} max-h-[90vh] overflow-y-auto`}
                onClick={(e) => e.stopPropagation()}
            >
                <div className={`flex items-center justify-between border-b ${accent ? "border-signal" : "border-hairline"} px-5 py-3`}>
                    <div>
                        <div className="overline">Action</div>
                        <div className="heading-display text-lg">{title}</div>
                    </div>
                    <button onClick={onClose} className="text-snow-dim hover:text-signal" data-testid="modal-close-btn">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                <div className="p-5">{children}</div>
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return <div><label className="overline block mb-2">{label}</label>{children}</div>;
}

function relativeTime(iso) {
    try {
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return `${Math.floor(diff)}s ago`;
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        return `${Math.floor(diff / 86400)}d ago`;
    } catch {
        return iso;
    }
}
